/**
 * Recent-customers store — houdt de laatste 10 bezochte klanten per device bij
 * in localStorage. Gevoed door klanten-store.fetchCustomerDetail.
 *
 * Defensive persistence-patroon zoals in phone-store: try/catch rondom
 * localStorage, JSON-parse validatie, stille fallback bij private mode.
 */

import { create } from 'zustand';
import type { BrandFlags } from './klanten-store';

const STORAGE_KEY = 'office-app:recent-customers';
const MAX = 10;

export type RecentCustomerType = 'account_person' | 'account_business' | 'lead';

export interface RecentCustomer {
  id: string;
  type: RecentCustomerType;
  name: string;
  email: string;
  phone: string;
  city: string;
  postcode: string;
  brandFlags?: BrandFlags;
  lastViewedAt: string;
}

function isValidEntry(x: unknown): x is RecentCustomer {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.id === 'string' &&
    typeof o.type === 'string' &&
    typeof o.name === 'string' &&
    typeof o.lastViewedAt === 'string'
  );
}

function loadRecent(): RecentCustomer[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isValidEntry).slice(0, MAX);
  } catch {
    return [];
  }
}

function persistRecent(list: RecentCustomer[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch {
    // swallow — localStorage may be disabled (private mode)
  }
}

interface RecentCustomersState {
  recent: RecentCustomer[];
  push: (c: Omit<RecentCustomer, 'lastViewedAt'>) => void;
  clear: () => void;
}

export const useRecentCustomersStore = create<RecentCustomersState>()((set, get) => ({
  recent: loadRecent(),

  push: (c) => {
    const now = new Date().toISOString();
    const next: RecentCustomer = { ...c, lastViewedAt: now };
    const current = get().recent;
    const deduped = current.filter(r => r.id !== c.id);
    const updated = [next, ...deduped].slice(0, MAX);
    persistRecent(updated);
    set({ recent: updated });
  },

  clear: () => {
    persistRecent([]);
    set({ recent: [] });
  },
}));
